import { Alert, Form, Radio, Select, Space, Typography } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TrademarkContext from "../../../context/trademark/context";
import { BooleanStatus } from "../../../models/product/enum";
import { ITrademarkProductStatusRequest } from "../../../models/trademark/request";
import { successNotification } from "../../../utils/helpers/notification";
import Button from "../../general/antd/button";
import Modal from "../../general/antd/modal";

interface Props {
  trademarkId: number;
  trademarkName: string;
  onClose: () => void;
}

const ProductStatusModal: React.FC<Props> = ({
  trademarkId,
  trademarkName,
  onClose,
}) => {
  const { t } = useTranslation();
  const { actions } = useContext(TrademarkContext);
  const [status, setStatus] = useState<BooleanStatus>();
  const [scope, setScope] = useState<"global" | "store">();
  const [storeId, setStoreId] = useState<number>();
  const [stores, setStores] = useState<{ id: number; name: string }[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storesFailed, setStoresFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState(false);
  const [preview, setPreview] = useState<{
    request: ITrademarkProductStatusRequest;
    count: number;
  }>();
  const loadStores = useRef(actions.getProductStatusStores);

  useEffect(() => {
    if (scope !== "store") return;
    let active = true;
    setStoresLoading(true);
    setStoresFailed(false);
    loadStores
      .current(trademarkId)
      .then((data) => {
        if (active) setStores(data);
      })
      .catch(() => {
        if (active) setStoresFailed(true);
      })
      .finally(() => {
        if (active) setStoresLoading(false);
      });
    return () => {
      active = false;
    };
  }, [scope, trademarkId]);

  const request: ITrademarkProductStatusRequest | undefined =
    status && scope
      ? scope === "global"
        ? { status, scope }
        : storeId
          ? { status, scope, storeId }
          : undefined
      : undefined;

  const review = async () => {
    if (!request || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(false);
    try {
      const count = await actions.previewProductStatus(trademarkId, request);
      setPreview({ request, count });
    } catch {
      setError(true);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!preview || preview.count === 0 || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(false);
    try {
      const count = await actions.changeProductStatus(
        trademarkId,
        preview.request,
      );
      successNotification(t("trademark_product_status_done", { count }));
      onClose();
    } catch {
      // A failed response can be ambiguous; require a fresh preview before retry.
      setPreview(undefined);
      setError(true);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={t("trademark_product_status_title")}
      onCancel={onClose}
      closable={!busy}
      keyboard={!busy}
      footer={
        <Space wrap>
          <Button disabled={busy} onClick={onClose}>
            {t("cancel")}
          </Button>
          {preview ? (
            <>
              <Button
                key="back"
                disabled={busy}
                onClick={() => setPreview(undefined)}
              >
                {t("trademark_product_status_back")}
              </Button>
              <Button
                key="confirm"
                type="primary"
                danger={preview.request.status === BooleanStatus.INACTIVE}
                loading={busy}
                disabled={preview.count === 0}
                onClick={confirm}
              >
                {t(
                  preview.request.status === BooleanStatus.ACTIVE
                    ? "trademark_product_status_confirm_activate"
                    : "trademark_product_status_confirm_deactivate",
                )}
              </Button>
            </>
          ) : (
            <Button
              key="review"
              type="primary"
              loading={busy}
              disabled={
                !request ||
                (scope === "store" && (storesLoading || storesFailed))
              }
              onClick={review}
            >
              {t("trademark_product_status_review")}
            </Button>
          )}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Typography.Text strong>{trademarkName}</Typography.Text>
        {error && (
          <Alert
            type="error"
            showIcon
            message={t("trademark_product_status_error")}
          />
        )}
        {preview ? (
          <>
            <Typography.Text>
              {t(
                preview.request.status === BooleanStatus.ACTIVE
                  ? "trademark_product_status_activate"
                  : "trademark_product_status_deactivate",
              )}
              {" · "}
              {preview.request.scope === "global"
                ? t("trademark_product_status_global")
                : stores.find((store) => store.id === preview.request.storeId)
                    ?.name}
            </Typography.Text>
            <Alert
              type={preview.count === 0 ? "info" : "warning"}
              showIcon
              message={t(
                preview.count === 0
                  ? "trademark_product_status_empty"
                  : "trademark_product_status_affected",
                { count: preview.count },
              )}
            />
            {preview.request.status === BooleanStatus.ACTIVE && (
              <Alert
                type="warning"
                showIcon
                message={t("trademark_product_status_activation_note")}
              />
            )}
            <Typography.Paragraph type="secondary">
              {t("trademark_product_status_scope_note")}
            </Typography.Paragraph>
            <Typography.Text type="secondary">
              {t("trademark_product_status_count_note")}
            </Typography.Text>
          </>
        ) : (
          <Form layout="vertical">
            <Form.Item label={t("trademark_product_status_operation")} required>
              <Radio.Group
                value={status}
                disabled={busy}
                onChange={(event) => setStatus(event.target.value)}
              >
                <Radio value={BooleanStatus.ACTIVE}>
                  {t("trademark_product_status_activate")}
                </Radio>
                <Radio value={BooleanStatus.INACTIVE}>
                  {t("trademark_product_status_deactivate")}
                </Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item label={t("trademark_product_status_scope")} required>
              <Radio.Group
                value={scope}
                disabled={busy}
                onChange={(event) => {
                  setScope(event.target.value);
                  setStoreId(undefined);
                }}
              >
                <Radio value="global">
                  {t("trademark_product_status_global")}
                </Radio>
                <Radio value="store">
                  {t("trademark_product_status_store")}
                </Radio>
              </Radio.Group>
            </Form.Item>
            {scope === "store" && (
              <Form.Item label={t("store")} required>
                <Select
                  showSearch
                  allowClear
                  value={storeId}
                  disabled={busy || storesLoading}
                  loading={storesLoading}
                  placeholder={t("select_store")}
                  optionFilterProp="label"
                  onChange={setStoreId}
                  options={stores.map((store) => ({
                    value: store.id,
                    label: store.name,
                  }))}
                />
                {storesFailed && (
                  <Alert
                    type="error"
                    showIcon
                    message={t("trademark_product_status_stores_error")}
                    action={
                      <Button size="small" onClick={() => setScope(undefined)}>
                        {t("trademark_product_status_back")}
                      </Button>
                    }
                  />
                )}
              </Form.Item>
            )}
          </Form>
        )}
      </Space>
    </Modal>
  );
};

export default ProductStatusModal;
